import React, { createContext, useContext } from 'react';
import { useSnackbar as useNotistackSnackbar, VariantType, OptionsObject } from 'notistack';

interface SnackbarContextProps {
  showMessage: (message: string, options?: OptionsObject) => void;
  showSuccess: (message: string, options?: OptionsObject) => void;
  showError: (message: string, options?: OptionsObject) => void;
  showWarning: (message: string, options?: OptionsObject) => void;
  showInfo: (message: string, options?: OptionsObject) => void;
}

const SnackbarContext = createContext<SnackbarContextProps | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useNotistackSnackbar();

  const showMessage = (message: string, options?: OptionsObject) => {
    enqueueSnackbar(message, {
      variant: 'default',
      autoHideDuration: 3000,
      ...options,
    });
  };

  const showWithVariant = (message: string, variant: VariantType, options?: OptionsObject) => {
    enqueueSnackbar(message, {
      variant,
      autoHideDuration: variant === 'error' ? 5000 : 3000,
      ...options,
    });
  };

  const showSuccess = (message: string, options?: OptionsObject) => {
    showWithVariant(message, 'success', options);
  };

  const showError = (message: string, options?: OptionsObject) => {
    showWithVariant(message, 'error', options);
  };

  const showWarning = (message: string, options?: OptionsObject) => {
    showWithVariant(message, 'warning', options);
  };

  const showInfo = (message: string, options?: OptionsObject) => {
    showWithVariant(message, 'info', options);
  };

  const contextValue: SnackbarContextProps = {
    showMessage,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
    </SnackbarContext.Provider>
  );
};

export const useAppSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useAppSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export default SnackbarContext;

